import Clickable from "components/Clickable";
import { CmsResponsiveImage } from "components/CmsResponsiveImage";
import { LoadingOverlay } from "components/Loading/LoadingOverlay";
import { TailoredCloudinaryImageProps } from "components/ResponsiveImage";
import { Tooltip } from "components/Tooltip/Tooltip";
import {
    PaddingValue,
    getProductImageTransforms,
    imagePlaceholder,
    usePreviousValue,
} from "helpers/utils";
import { useState } from "react";
import { Pressable } from "react-aria";

import Info from "../../public/svg/info.svg";

import styles from "./configured-product-image.module.scss";

interface Props {
    images: (string | null | undefined)[];
    noApplicableImage: boolean;
    caption?: React.ReactNode;
    pageLoading: boolean;
    pad: PaddingValue;
}

export const ConfiguredProductImage = ({
    images: nullishImages,
    noApplicableImage,
    caption,
    pageLoading,
    pad,
}: Props) => {
    /**
     * A signal that the image is loading. This lets us account for the difference between the
     * server giving us the image URL and the image itself being loaded.
     */
    const [imageLoading, setImageLoading] = useState(false);
    const images = nullishImages.filter(function isNullish<T>(
        x: T,
    ): x is NonNullable<T> {
        return !!x;
    });
    const previousImages = usePreviousValue(images);

    /**
     * The placeholder image needs to be uploaded instead of fetched, and it doesn't need the
     * special treatment that the product images are getting.
     */
    const settings: TailoredCloudinaryImageProps =
        images.length === 0
            ? { type: "upload" }
            : getProductImageTransforms(pad);

    if (images.length === 0) images[0] = imagePlaceholder;

    // Tailoring to a specific use case (which happens to cover most of the product pages)
    if (
        imageLoading === false &&
        images.length === 1 &&
        previousImages.length === 1 &&
        images[0] !== previousImages[0]
    ) {
        setImageLoading(true);
    }

    return (
        <figure className={styles.image}>
            <div className={styles.imageWrapper}>
                {/**
                 * Show it as loading regardless of what is loading
                 */}
                {(pageLoading || imageLoading) && (
                    <LoadingOverlay visuallyHiddenText="Image is loading…" />
                )}
                {images.length === 1 && (
                    <CmsResponsiveImage
                        cmsImage={{ url: images[0] }}
                        sizes="(max-width: 768px) 100vw, (min-width: 768px) 50vw"
                        cldSrcSet={[600, 1200, 1800, 2400]}
                        className={styles.singleImage}
                        cloudinaryProps={settings}
                        onLoad={() =>
                            setTimeout(() => setImageLoading(false), 100)
                        }
                        onError={() => setImageLoading(false)}
                    />
                )}
                {images.length > 1 && (
                    <div className={styles.doubleImageWrapper}>
                        {images.slice(0, 2).map((image, index) => (
                            <CmsResponsiveImage
                                key={index}
                                cmsImage={{ url: image }}
                                sizes="(max-width: 768px) 50vw, (min-width: 768px) 25vw"
                                cldSrcSet={[600, 1200, 1800, 2400]}
                            />
                        ))}
                    </div>
                )}
            </div>
            <figcaption>
                {noApplicableImage && (
                    <p className={styles.noApplicableImage}>
                        Image unavailable in your selections
                        <Tooltip
                            label={`
                Emtek does not have images of every possible combination, but we are able to show
                you something close.
              `}
                        >
                            <Pressable>
                                <Clickable unstyled={true}>
                                    <Info />
                                </Clickable>
                            </Pressable>
                        </Tooltip>
                    </p>
                )}
                {!!caption && (
                    <p>
                        <strong>Shown:</strong> {caption}
                    </p>
                )}
            </figcaption>
        </figure>
    );
};
